/**
 * Las preguntas con las que se describe un teléfono, en un solo lugar.
 *
 * POR QUE ESTAN JUNTAS Y NO EN CADA FORMULARIO
 * -------------------------------------------
 * Modelo, memoria y color se preguntan en tres pantallas —el alta de sellados,
 * el asistente de usados y la edición del catálogo— y las tres tienen que
 * preguntar lo mismo de la misma manera. Cuando cada una tenía sus propios
 * controles, una pedía el modelo con chips y otra con un campo de texto, y la
 * tercera no preguntaba el color en ningún lado: se editaba un iPhone y no
 * había dónde elegirlo.
 *
 * Son controles, no un formulario: no tienen estado propio ni saben qué se
 * está cargando. Cada pantalla les pasa lo que tiene y recibe lo que se
 * eligió.
 *
 * NINGUNO PIDE UN NOMBRE
 * ----------------------
 * El nombre sale de estas respuestas (`nombreDeEquipo`). Escribirlo a mano era
 * lo que dejaba cuatro productos distintos para el mismo teléfono —"16 pro
 * max", "16 PROMAX", "16 Pro Max"— y un nombre mal escrito no lo puede
 * arreglar nada después: es lo que el buscador busca.
 */
import { useState } from "react";

import { MuestraColor } from "@/components/catalogo/MuestraColor";
import { colorDeIphone, coloresDelModelo } from "@/components/catalogo/colores";
import { GrupoCampo } from "@/components/ui/campo";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";

import {
  MARCAS_DE_CELULAR,
  MEMORIAS,
  armarModeloElegido,
  partirModeloElegido,
  partirOtraMarca,
  type ModeloElegido,
} from "./equipo";
import { etiquetaCapacidad } from "./logica";
import { memoriasDelModelo } from "./memorias";
import { GENERACIONES, etiquetaVariante, variantesDe } from "./modelos";

const OPCIONES_MEMORIA = MEMORIAS.map((gb) => ({
  valor: String(gb),
  etiqueta: etiquetaCapacidad(gb),
}));

/**
 * La salida del selector de modelo para lo que no es un iPhone.
 *
 * El lineup de Apple es una lista cerrada, pero por el local pasan usados de
 * otras marcas. Sin esta opción, cargar un Samsung sería imposible y cualquiera
 * terminaría eligiendo el iPhone que más se parece — que es peor que un nombre
 * escrito a mano, porque queda en la lista del lineup.
 *
 * Se llama «Otra marca» y no «Otro»: no elegir un iPhone ES decir que es de
 * otra marca, y eso decide a qué solapa va, con o sin el nombre de por medio.
 */
const OTRO = "_otro";

/**
 * Con qué texto se guarda un color de la tabla.
 *
 * Es la etiqueta de Apple en mayúsculas, sin el "(PRODUCT)" del rojo: así el
 * valor guardado coincide siempre con la clave de la tabla ("BLACK TITANIUM",
 * "RED"), que es lo que ya hay cargado en la base.
 */
function valorDeColor(etiqueta: string): string {
  return etiqueta.toUpperCase().replace(/\(PRODUCT\)\s*/g, "");
}

export interface SelectorDeModeloProps {
  /** El modelo tal cual va al nombre: "17 PRO MAX", "SAMSUNG S22". */
  modelo: string;
  onChange: (modelo: string) => void;
  error?: string;
  /** El texto de ayuda de la generación. Cambia entre sellados y usados. */
  ayuda?: string;
  /**
   * Ofrecer «Otro», que abre un campo de texto.
   *
   * Solo donde otra marca es una carga legítima: por el local pasan usados
   * Samsung, y sin esta salida cargarlos sería imposible. En el alta de un
   * iPhone SELLADO no existe —lo que se vende sellado es el lineup de Apple— y
   * ofrecer un campo libre ahí es volver a abrir la puerta que este cambio
   * cerró: un modelo escrito a mano que nada puede corregir después.
   */
  permiteOtro?: boolean;
  autoFocus?: boolean;
}

/**
 * El modelo, en dos preguntas: la generación y la variante.
 *
 * Es como se pregunta en el mostrador —"¿el 16? ¿el Pro Max?"— y además parte
 * una lista de cuarenta y pico de modelos en dos de diez y de cinco, que entran
 * en la pantalla de un teléfono sin scrollear.
 *
 * Un equipo YA CARGADO abre con los chips donde corresponde, y si su modelo no
 * es del lineup abre en «Otro» con lo que estaba escrito. Editarle el precio a
 * un Samsung no puede obligar a convertirlo en un iPhone.
 */
export function SelectorDeModelo({
  modelo,
  onChange,
  error,
  ayuda,
  permiteOtro = false,
  autoFocus = false,
}: SelectorDeModeloProps): JSX.Element {
  const elegido = partirModeloElegido(modelo);
  // Recién tocada, «Otra marca» no tiene texto que la delate: sin recordarla,
  // el chip se apagaba al tocarlo y no aparecía dónde escribir.
  const [otraTocada, setOtraTocada] = useState(false);
  const esOtro = permiteOtro && elegido.generacion === "" && (otraTocada || modelo.trim() !== "");

  function cambiar(patch: Partial<ModeloElegido>): void {
    onChange(armarModeloElegido({ ...elegido, ...patch }));
  }

  const variantes = variantesDe(elegido.generacion);

  return (
    <>
      <GrupoCampo label="Modelo" requerido ayuda={ayuda} error={error}>
        <Segmented
          apariencia="chips"
          tamano="md"
          etiqueta="Generación"
          opciones={[
            ...GENERACIONES.map((g) => ({ valor: g, etiqueta: g })),
            ...(permiteOtro ? [{ valor: OTRO, etiqueta: "Otra marca" }] : []),
          ]}
          valor={esOtro ? OTRO : elegido.generacion}
          // Cambiar de generación borra la variante: el "PRO MAX" del 16 no es
          // el del 15, y una variante que la generación nueva no tiene deja un
          // modelo que no existe.
          onChange={(v) => {
            setOtraTocada(v === OTRO);
            // Pasar de un iPhone a otra marca arranca en blanco: el «16 PRO»
            // no es el modelo de ningún Samsung.
            if (v === OTRO) onChange(elegido.libre);
            else cambiar({ generacion: v, variante: "", libre: "" });
          }}
        />
      </GrupoCampo>

      {esOtro ? (
        <CamposDeOtraMarca modelo={modelo} onChange={onChange} autoFocus={autoFocus} />
      ) : null}

      {/* Una generación con una sola variante no es una pregunta. */}
      {variantes.length > 1 ? (
        <GrupoCampo label="Variante" ayuda="«Base» es el modelo a secas, sin Pro ni Plus.">
          <Segmented
            apariencia="chips"
            tamano="md"
            etiqueta="Variante"
            opciones={variantes.map((v) => ({ valor: v, etiqueta: etiquetaVariante(v) }))}
            valor={elegido.variante}
            onChange={(v) => cambiar({ variante: v })}
          />
        </GrupoCampo>
      ) : null}
    </>
  );
}

const OTRA_MARCA = "_otra";

export interface CamposDeOtraMarcaProps {
  /** La marca y el modelo juntos, como viajan: "SAMSUNG A57 5G". */
  modelo: string;
  onChange: (modelo: string) => void;
  error?: string;
  autoFocus?: boolean;
  /**
   * Ofrecer la marca iPhone, para los anteriores al 11 que no tienen botón en
   * el lineup. No en el alta de celulares nuevos: un sellado es del lineup.
   */
  conIphone?: boolean;
}

/**
 * Un teléfono que no es iPhone: la marca y el modelo, por separado.
 *
 * Antes era UN campo que pedía «la marca adelante», y nada lo hacía cumplir:
 * sin la marca, o con una que el sistema no conocía, el armador le ponía
 * «IPHONE» delante. Ahora la marca es su propia pregunta, con botones para
 * las que pasan por el local, y el modelo es lo que queda.
 *
 * Los dos campos viven acá y viajan juntos (`partirOtraMarca` los separa). Sin
 * marca no viaja nada: un «15» escrito solo se leería como el iPhone 15.
 */
export function CamposDeOtraMarca({
  modelo,
  onChange,
  error,
  autoFocus = false,
  conIphone = true,
}: CamposDeOtraMarcaProps): JSX.Element {
  const [campos, setCampos] = useState(() => partirOtraMarca(modelo));
  const [enviado, setEnviado] = useState(modelo);
  const [escribeMarca, setEscribeMarca] = useState(
    () =>
      campos.marca !== "" &&
      !(MARCAS_DE_CELULAR as ReadonlyArray<string>).includes(campos.marca) &&
      !(conIphone && campos.marca === "IPHONE"),
  );
  // Si el formulario cambia el modelo desde afuera —se abrió otra ficha, se
  // vació—, los campos lo siguen.
  if (modelo !== enviado) {
    setEnviado(modelo);
    setCampos(partirOtraMarca(modelo));
  }

  // «Modelo de Samsung» y no «Modelo» a secas: adentro del selector de usados
  // arriba ya hay un «Modelo» —los botones de iPhone— y eran dos iguales.
  const etiquetaModelo = campos.marca
    ? `Modelo de ${campos.marca === "IPHONE" ? "iPhone" : campos.marca.charAt(0) + campos.marca.slice(1).toLowerCase()}`
    : "Modelo";

  function poner(c: { marca: string; resto: string }): void {
    const armado = c.marca ? [c.marca, c.resto.trim()].filter(Boolean).join(" ") : "";
    setCampos(c);
    setEnviado(armado);
    onChange(armado);
  }

  return (
    // Un bloque propio con su aire: son dos preguntas, y adentro de la grilla
    // del alta quedaban pegadas —«Modelo» encima de los botones de la marca—.
    <div className="space-y-4">
      <GrupoCampo label="Marca" requerido error={error}>
        <Segmented
          apariencia="chips"
          // Del alto del campo de texto de abajo: pocos botones, entran igual.
          tamano="md"
          etiqueta="Marca"
          opciones={[
            ...MARCAS_DE_CELULAR.map((m) => ({
              valor: m,
              etiqueta: m.charAt(0) + m.slice(1).toLowerCase(),
            })),
            ...(conIphone ? [{ valor: "IPHONE", etiqueta: "iPhone anterior al 11" }] : []),
            { valor: OTRA_MARCA, etiqueta: "Otra" },
          ]}
          valor={escribeMarca ? OTRA_MARCA : campos.marca}
          onChange={(v) => {
            setEscribeMarca(v === OTRA_MARCA);
            poner({ ...campos, marca: v === OTRA_MARCA ? "" : v });
          }}
        />
        {escribeMarca ? (
          <Input
            aria-label="¿Qué marca?"
            autoFocus
            placeholder="NOKIA"
            value={campos.marca}
            // Una palabra: la marca es la primera palabra de lo que viaja.
            onChange={(e) =>
              poner({ ...campos, marca: e.target.value.replace(/\s+/g, "").toUpperCase() })
            }
          />
        ) : null}
      </GrupoCampo>
      <GrupoCampo
        label={etiquetaModelo}
        requerido
        ayuda="Sin la marca ni la memoria: «A57 5G», «Edge 40»."
      >
        <Input
          aria-label={etiquetaModelo}
          autoFocus={autoFocus}
          placeholder="A57 5G"
          value={campos.resto}
          onChange={(e) => poner({ ...campos, resto: e.target.value.toUpperCase() })}
        />
      </GrupoCampo>
    </div>
  );
}

export interface SelectorDeMemoriaProps {
  /**
   * El modelo elegido: "15 PRO". Ofrece solo las memorias en que vino ese
   * modelo; uno que la tabla no conoce (otra marca, o todavía sin elegir)
   * ofrece todas.
   */
  modelo?: string;
  memoriaGb: number | null;
  onChange: (gb: number) => void;
  error?: string;
}

export function SelectorDeMemoria({
  modelo = "",
  memoriaGb,
  onChange,
  error,
}: SelectorDeMemoriaProps): JSX.Element {
  // Un 15 base no viene en 1 TB: ofrecerlo es invitar a cargar un teléfono que
  // Apple no hizo. La memoria ya guardada se muestra igual aunque la tabla no
  // la tenga, para que editar una ficha vieja no la haga desaparecer.
  const delModelo = memoriasDelModelo(modelo);
  const opciones =
    delModelo.length === 0
      ? OPCIONES_MEMORIA
      : [...new Set([...delModelo, ...(memoriaGb === null ? [] : [memoriaGb])])]
          .sort((a, b) => a - b)
          .map((gb) => ({ valor: String(gb), etiqueta: etiquetaCapacidad(gb) }));
  return (
    <GrupoCampo label="Memoria" requerido error={error}>
      <Segmented
        apariencia="chips"
        tamano="md"
        etiqueta="Memoria"
        opciones={opciones}
        valor={memoriaGb === null ? "" : String(memoriaGb)}
        onChange={(v) => onChange(Number(v))}
      />
    </GrupoCampo>
  );
}

export interface SelectorDeColorProps {
  /** Para saber en qué colores viene. Sin modelo no hay colores que ofrecer. */
  modelo: string;
  color: string;
  onChange: (color: string) => void;
  /**
   * Un sellado sin color es la fila de lista de precios del modelo: viene en
   * todos. Una unidad usada que está en la mano tiene un color y nada más, así
   * que ahí la opción no existe.
   */
  admiteTodos?: boolean;
  /**
   * Dejar escribir el color cuando el modelo no tiene lista.
   *
   * Solo para los modelos que la tabla no conoce —un Samsung—, y por eso viaja
   * junto con `permiteOtro` del selector de modelo. En el lineup de Apple el
   * color es una lista cerrada: el 16 Pro viene en cuatro y ninguno se llama
   * «negro». Escrito a mano entraba como "BLACK", "Black", "NEGRO" y
   * "space black", y el cuadradito de la lista no podía dibujar ninguno.
   */
  permiteEscribir?: boolean;
  ayuda?: string;
}

/**
 * El color, elegido de los que ese modelo tiene.
 *
 * Los colores de Apple no son una lista libre: el 16 Pro viene en cuatro y
 * ninguno se llama "negro". Escritos a mano entraban como "BLACK", "Black",
 * "NEGRO" y "space black", y el cuadradito de la lista no podía dibujar
 * ninguno. Elegidos de la tabla del modelo, el color que se guarda es siempre
 * uno que existe.
 *
 * Un modelo que la tabla no conoce —un Samsung— cae en un campo de texto: es
 * la respuesta honesta, porque la alternativa sería ofrecerle los colores de
 * otro teléfono.
 */
export function SelectorDeColor({
  modelo,
  color,
  onChange,
  admiteTodos = false,
  permiteEscribir = false,
  ayuda,
}: SelectorDeColorProps): JSX.Element {
  const colores = coloresDelModelo(`IPHONE ${modelo} 128 GB`);
  const elegido = colorDeIphone(color);

  if (colores.length === 0) {
    // Sin modelo elegido —o con la variante a medias— no hay colores que
    // ofrecer, y un campo de texto ahí es peor que nada: invita a escribir el
    // color de un teléfono que todavía no se dijo cuál es. El control aparece
    // cuando la pregunta de arriba está contestada.
    if (!permiteEscribir || modelo.trim() === "") return <></>;
    return (
      <GrupoCampo label="Color" ayuda={ayuda ?? "Tal cual lo llama el fabricante."}>
        <Input
          placeholder="BLACK"
          value={color}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
        />
      </GrupoCampo>
    );
  }

  return (
    <GrupoCampo label="Color" ayuda={ayuda}>
      <Segmented
        apariencia="chips"
        tamano="md"
        etiqueta="Color"
        opciones={[
          // «Todos» primero y es el default de un sellado: se pide en el color
          // que el cliente quiera, así que lo normal es no elegir ninguno.
          ...(admiteTodos ? [{ valor: "", etiqueta: "Todos" }] : []),
          ...colores.map((c) => ({
            valor: valorDeColor(c.etiqueta),
            etiqueta: c.etiqueta,
            muestra: c.hex,
          })),
        ]}
        // Cuál chip está marcado se decide por el color que la TABLA reconoce y
        // no por el texto guardado: en la base hay "DESERT" y "RED", que son
        // Desert Titanium y (PRODUCT)RED. Comparando el texto crudo, la ficha
        // de un equipo cargado abría sin ningún color marcado y parecía que se
        // había perdido.
        valor={
          elegido ? valorDeColor(elegido.etiqueta) : color.trim() === "" ? "" : color.toUpperCase()
        }
        onChange={onChange}
      />
      {/* El cuadradito del color elegido: el nombre solo no dice cuál es, y es
          el mismo dibujo con el que después aparece en la lista. */}
      {color.trim() ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <MuestraColor color={color} />
          {color.trim()}
        </p>
      ) : null}
    </GrupoCampo>
  );
}
